import { useQuery } from '@tanstack/react-query';
import type { CampaignPreview, CampaignSendResult, Paginated } from '@barbeiropro/contracts';
import type { OrgCapabilities } from '@barbeiropro/contracts';
import { api } from '@/lib/api';

export interface CustomerRow {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  preferredBarberId: string | null;
  preferredBarberName: string | null;
  marketingConsent: boolean;
  whatsappOptOut: boolean;
  tags: string[];
  lastVisitAt: string | null;
  createdAt: string;
  deletedAt: string | null;
}

export interface CustomerDetail extends Omit<CustomerRow, 'preferredBarberName'> {
  cpf: string | null;
  birthDate: string | null;
  preferences: string | null;
  notes: string | null;
  marketingConsentAt: string | null;
  preferredBarberName: string | null;
}

export interface CustomerHistory {
  totals: {
    completedVisits: number;
    totalSpentCents: number;
    averageTicketCents: number;
    lastVisitAt: string | null;
    topBarberId: string | null;
    topBarberName: string | null;
  };
  appointments: {
    id: string;
    startsAt: string;
    status: 'requested' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
    priceCents: number;
    paidCents: number | null;
    serviceName: string;
    barberName: string;
  }[];
  payments: {
    id: string;
    totalCents: number;
    discountCents: number;
    status: string;
    createdAt: string;
    methods: { kind: string; amountCents: number }[];
  }[];
  loyaltyBalance: number;
}

export interface LoyaltyProgram {
  mode: 'points' | 'visits';
  active: boolean;
  pointsPerReal: number;
  rewardThreshold: number;
  rewardDescription: string;
  rewardValueCents: number;
}

export interface CustomerLoyalty {
  balance: number;
  lifetimeEarned: number;
  program: { active: boolean; mode: 'points' | 'visits'; rewardThreshold: number; rewardDescription: string };
  canRedeem: boolean;
  transactions: { id: string; kind: 'earn' | 'redeem' | 'adjust' | 'reverse'; points: number; reason: string | null; createdAt: string }[];
}

export interface Plan {
  id: string;
  name: string;
  description: string | null;
  kind: 'recurring' | 'package';
  priceCents: number;
  billingIntervalMonths: number | null;
  validityDays: number | null;
  usesIncluded: number | null;
  serviceIds: string[];
  active: boolean;
}

export interface Subscription {
  id: string;
  planId: string;
  planName: string;
  planKind: 'recurring' | 'package';
  priceCents: number;
  status: 'pending_payment' | 'active' | 'expired' | 'cancelled';
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  usesRemaining: number | null;
  usesIncluded: number | null;
}

export interface Coupon {
  id: string;
  code: string;
  description: string | null;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  maxRedemptions: number | null;
  maxPerCustomer: number;
  validFrom: string;
  validUntil: string;
  minAmountCents: number;
  serviceIds: string[];
  active: boolean;
  redemptionsCount: number;
}

export interface Campaign {
  id: string;
  name: string;
  subject: string;
  body: string;
  audience: { inactiveDays?: number | null; barberId?: string | null; tags?: string[] };
  couponId: string | null;
  status: 'draft' | 'sending' | 'sent';
  recipientsCount: number;
  sentAt: string | null;
  createdAt: string;
}

export interface ServiceOption {
  id: string;
  name: string;
  priceCents: number;
}

export const useServiceOptions = () =>
  useQuery({
    queryKey: ['customers', 'service-options'],
    queryFn: async () => (await api.get<Paginated<ServiceOption>>('/services', { pageSize: 100, sort: 'name' })).data,
    staleTime: 60_000,
  });

export const useActivePlans = (enabled = true) =>
  useQuery({
    queryKey: ['loyalty', 'plans', 'active'],
    enabled,
    queryFn: async () => (await api.get<Paginated<Plan>>('/subscription-plans', { pageSize: 100, active: 'true' })).data,
  });

/** Disponibilidade do envio de e-mail para o tenant (só estado funcional). */
export const useEmailAvailability = () =>
  useQuery({
    queryKey: ['org', 'capabilities'],
    queryFn: async () => (await api.get<OrgCapabilities>('/org/capabilities')).email,
    staleTime: 60_000,
  });

export type { CampaignPreview, CampaignSendResult };

export const SUB_STATUS: Record<Subscription['status'], string> = {
  pending_payment: 'Aguardando pagamento',
  active: 'Ativa',
  expired: 'Vencida',
  cancelled: 'Cancelada',
};
